import {NumericInput, NumericInputProps} from "@blueprintjs/core";
import {HTMLInputProps} from "@blueprintjs/core/src/common/props";
import {styled} from "@mui/material";
import React, {useCallback} from "react";

type StyledNumericInputProps = Omit<HTMLInputProps, "size"> &
  NumericInputProps & {
    allowNegative?: boolean;
    onEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  };

const StyledNumericInput = (props: StyledNumericInputProps) => {
  const {allowNegative = true, ...restProps} = props;

  const onKeyDownProxy = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && restProps.onEnter) {
        restProps.onEnter(e);
        e.stopPropagation();
        return;
      }

      // Prevent entering 'e' or 'E' characters which are valid in scientific notation but not desired here
      if (e.key === "e" || e.key === "E") {
        e.preventDefault();
      }

      // Prevent entering minus sign if allowNegative is false
      if (!allowNegative && (e.key === "-" || e.key === "Minus")) {
        e.preventDefault();
      }

      // Check min/max value constraints on numeric key press
      if (e.key >= "0" && e.key <= "9") {
        const currentInput = e.currentTarget;
        const currentValue = currentInput.value;
        const selectionStart = currentInput.selectionStart || 0;
        const selectionEnd = currentInput.selectionEnd || 0;

        // Construct the new value after key press
        const newValue = currentValue.slice(0, selectionStart) + e.key + currentValue.slice(selectionEnd);

        const numericValue = parseFloat(newValue);

        if (!isNaN(numericValue)) {
          if (restProps.max !== undefined && numericValue > restProps.max) {
            e.preventDefault();
            return;
          }
        }
      }

      restProps.onKeyDown?.(e);
    },
    [allowNegative, restProps]
  );

  const onPasteProxy = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pasteData = e.clipboardData.getData("text");

      // Check if the pasted text contains 'e' or 'E'
      if (pasteData.toLowerCase().includes("e")) {
        e.preventDefault();
        return;
      }

      // Check if the pasted text contains minus sign when allowNegative is false
      if (!allowNegative && pasteData.includes("-")) {
        e.preventDefault();
        return;
      }

      // Check min/max value constraints
      const pastedValue = parseFloat(pasteData);
      if (!isNaN(pastedValue)) {
        if (restProps.max !== undefined && pastedValue > restProps.max) {
          e.preventDefault();
          return;
        }
      }

      restProps.onPaste?.(e);
    },
    [allowNegative, restProps]
  );

  const onBlurProxy = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const inputValue = e.currentTarget.value;
      const numericValue = parseFloat(inputValue);
      if (!isNaN(numericValue)) {
        if (restProps.min !== undefined && numericValue < restProps.min) {
          if (restProps.onValueChange) {
            restProps.onValueChange(restProps.min, restProps.min.toString(), e.currentTarget);
          }
        }
      }

      restProps.onBlur?.(e);
    },
    [restProps]
  );

  return (
    <StyledNumeric
      buttonPosition="none"
      allowNumericCharactersOnly
      selectAllOnFocus
      {...restProps}
      onKeyDown={onKeyDownProxy}
      onPaste={onPasteProxy}
      onBlur={onBlurProxy}
      value={restProps.value ?? ""}
    />
  );
};

export default StyledNumericInput;

const StyledNumeric = styled(NumericInput)`
  input {
    border-radius: 6px;
    box-shadow: inset 0 0 0 1px rgba(17, 20, 24, 0.3);
    text-align: center;
    padding: 0 10px;
  }
`;
